import { useMemo } from "react";
import { Button } from "../../@/components/ui/button";
import { Skeleton } from "../../@/components/ui/skeleton";
import { EmptyState } from "./emptyState";
import { Link } from "@tanstack/react-router";
import { BookOpenIcon, ArrowUpRight } from "lucide-react";

// Example: You may want to pass these as props or fetch from your state/store
// pendingLogs: Array of attendance log objects
// isLoading: boolean

const getLogDateValue = (log = {}) => log.dateRaw || log.date || log.sessionDate;

const toValidDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date;
};

const isWithinLastDays = (date, days) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - Math.max(0, Number(days) || 0));
  return date >= start && date <= now;
};

const parsePresentTotal = (value) => {
  if (!value || typeof value !== "string") return null;
  const [presentRaw, totalRaw] = value.split("/");
  const present = Number(presentRaw);
  const total = Number(totalRaw);
  if (!Number.isFinite(present) || !Number.isFinite(total)) return null;
  return { present, total };
};

const isPendingAttendanceLog = (log) => {
  if (!log) return false;
  const status = String(log.status || "").trim().toUpperCase();
  if (status === "COMPLETED") return false;

  const percent = Number(log.percent);
  if (Number.isFinite(percent) && percent >= 100) return false;

  const totals = parsePresentTotal(log.present_total);
  if (totals && totals.total > 0 && totals.present >= totals.total) return false;

  return true;
};

const PendingAttendanceLogsSection = ({
  isLoading,
  pendingLogs = [],
  onReview,
  onViewAll,
  // filters/showFilter kept for backward compatibility but intentionally unused.
  // eslint-disable-next-line no-unused-vars
  filters,
  // eslint-disable-next-line no-unused-vars
  showFilter,
}) => {
  const displayItems = useMemo(() => {
    const pendingOnly = (pendingLogs || []).filter(isPendingAttendanceLog);

    const last30Days = pendingOnly.filter((log) => {
      const logDate = toValidDate(getLogDateValue(log));
      return logDate ? isWithinLastDays(logDate, 30) : false;
    });

    const sortedNewestFirst = [...last30Days].sort((a, b) => {
      const aDate = toValidDate(getLogDateValue(a));
      const bDate = toValidDate(getLogDateValue(b));
      const aTime = aDate ? aDate.getTime() : -Infinity;
      const bTime = bDate ? bDate.getTime() : -Infinity;
      return bTime - aTime;
    });

    return sortedNewestFirst.slice(0, 5);
  }, [pendingLogs]);

  return (
    <div className="max-md:mt-3 border border-(--neutral-300) rounded-lg bg-(--neutral-50)">
      <div className="p-4">
        <h3 className="mb-1">Pending Attendance Logs</h3>
        <p className="text-(--neutral-800)">
          Review and complete pending attendance
        </p>
      </div>
      <div className="flex flex-col gap-[8px]">
        <div>
          {isLoading ? (
            <div>
              {/* Skeletons or loading state */}
              <div className="space-y-2 p-2">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <Skeleton
                    key={idx}
                    className="h-16 w-full rounded border border-(--neutral-300)"
                  />
                ))}
              </div>
            </div>
          ) : displayItems.length > 0 ? (
            displayItems.map((log, idx) => (
              <div
                key={log.id ?? idx}
                className={`flex items-center justify-between p-4 border border-(--neutral-300) ${
                  idx === 0
                    ? "bg-(--neutral-100)"
                    : "bg-(--neutral-50)"
                }`}
              >
                <div>
                  <div className="font-heading text-h6 mb-1">
                    {log.sessionTitle || "Untitled Session"}
                  </div>
                  <div className="text-sm font-body text-(--neutral-800)">
                    {log.dateLabel || log.dateRaw}
                  </div>
                </div>
                <Button
                  size=""
                  variant="outline"
                  className=""
                  onClick={() => onReview?.(log)}
                >
                  Review
                </Button>
              </div>
            ))
          ) : (
            <div className="flex flex-col justify-center items-center my-[12px] h-[180px] bg-(--neutral-100) font-body">
              <EmptyState
                icon={BookOpenIcon}
                description="All attendance logs are up to date."
              />
            </div>
          )}
        </div>
        <div className="mt-1 mb-2">
          <Link to="/live-classes">
            <Button
              variant="link"
              className="text-(--color-primary) w-full font-semibold hover:bg-transparent hover:text-(--color-accent)"
              onClick={onViewAll}
            >
              View All Attendance Logs
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PendingAttendanceLogsSection;
